import React, { useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import {
  Toolbar,
  Box,
  IconButton,
  Menu,
  Avatar,
  Grid,
  Divider,
  Typography,
} from "@mui/material";
import MuiAppBar from "@mui/material/AppBar";
import MenuIcon from "@mui/icons-material/Menu";
import AccountCircle from "@mui/icons-material/AccountCircle";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChangeCircleIcon from "@mui/icons-material/ChangeCircle";
import LocalPostOfficeIcon from "@mui/icons-material/LocalPostOffice";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import DirectionsCarFilledIcon from "@mui/icons-material/DirectionsCarFilled";
import MapsHomeWorkIcon from "@mui/icons-material/MapsHomeWork";
import ApartmentIcon from "@mui/icons-material/Apartment";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import { useAuth } from "../../hooks/useAuth";
import CustomListItem from "./CustomListItemNavbar";
import { useNavigate, useLocation } from "react-router-dom";
import ModalSettingCompany from "components/Modal/Pawn/Create/ModalSettingCompany";
import ModalChangePassword from "components/Modal/Pawn/Create/ModalChangePassword";
import ModalBuyPackage from "components/Modal/MarketPlace/ModalBuyPackage";
import { ShoppingBasket } from "@mui/icons-material";

const drawerWidth = 300;

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  width: `calc(100% - ${100}px)`, // Always subtract drawerWidth
  marginLeft: open ? drawerWidth : 0, // Adjust marginLeft based on open state
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  background: "#fff", // Set background color to white
  color: "#000", // Set text color to black
  boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.1)", // Add shadow
  ...(open && {
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Navbar = ({ open, handleDrawerOpen }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const { logout, user, packageTypes } = useAuth();
  const [menuItems, setMenuItems] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const [isModalCompanyOpen, setIsModalCompanyOpen] = useState(false);
  const [isModalChangePassword, setIsModalChangePassword] = useState(false);
  const [isOpenPackage, setIsOpenPackage] = useState(false);
  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleClose();
    logout();
  };

  const handleCompanyClose = () => {
    setIsModalCompanyOpen(!isModalCompanyOpen);
  };

  const handleChangePasswordClose = () => {
    setIsModalChangePassword(!isModalChangePassword);
  };

  const handleOpenPackage = () => {
    setIsOpenPackage(!isOpenPackage);
  };

  const defaultMenuItems = [
    ...(user.role !== "SuperAdminA" && user.role !== "SuperAdminM") ? [{
      text: "แพ็คเกจ",
      icon: <LocalPostOfficeIcon />,
      NavigateIcon: <NavigateNextIcon />,
      isDivider: true,
      onClick: () => setIsOpenPackage(true),
    }]: [],
    {
      text: "Marketplace",
      icon: <ShoppingBasket />,
      NavigateIcon: <NavigateNextIcon />,
      isDivider: (user.role === "SuperAdminA" || user.role === "SuperAdminM") ? true : false,
      onClick: () => window.open("/marketplace", "_blank"),
    },
    ...(user.role !== "SuperAdminA" && user.role !== "SuperAdminM") ? [{
      text: "ตั้งค่าบริษัท",
      icon: <ApartmentIcon />,
      NavigateIcon: <NavigateNextIcon />,
      onClick: () => setIsModalCompanyOpen(true),
    }]: [],
    {
      text: "เปลี่ยนรหัสผ่าน",
      icon: <ChangeCircleIcon />,
      NavigateIcon: <NavigateNextIcon />,
      onClick: () => setIsModalChangePassword(true),
      isDivider: true,
    },
    {
      text: "ออกจากระบบ",
      icon: <PowerSettingsNewIcon />,
      NavigateIcon: <NavigateNextIcon />,
      onClick: handleLogout,
    },
  ];

  useEffect(() => {
    const fetchPackagesData = async () => {
      try {
        // Sort packageTypes
        packageTypes.sort((a, b) => a - b);
        const packageTypeExists = [...new Set(packageTypes)];

        let newMenuItems = [];
        let accessSuperAdmin = ["admin", "SuperAdminA", "SuperAdminM"];
        let accessAdmin = ["admin", "owner", "AdminA", "AdminM"];

        // // Iterate over packageTypes in reverse order to ensure "โรงรับจำนำ" appears first
        for (let i = packageTypeExists.length - 1; i >= 0; i--) {
          const packageType = packageTypeExists[i];
          switch (packageType) {
            case 1:
              accessAdmin.includes(user.role) &&
                newMenuItems.unshift({
                  text: "โรงรับจำนำ/ร้านทั้วไป",
                  icon: <Inventory2Icon />,
                  path: "/pawn",
                });
              break;
            case 2:
              accessAdmin.includes(user.role) &&
                newMenuItems.unshift({
                  text: "รถมือสอง",
                  icon: <DirectionsCarFilledIcon />,
                  path: "/car",
                });
              break;
            case 3:
              accessAdmin.includes(user.role) &&
                newMenuItems.unshift({
                  text: "ที่ดิน",
                  icon: <MapsHomeWorkIcon />,
                  path: "/land",
                  isDivider: true,
                });
              break;
            default:
              break;
          }
        }

        if (accessSuperAdmin.includes(user.role)) {
          newMenuItems.push({
            text: "แอดมิน",
            icon: <ApartmentIcon />,
            path: "/admin",
          });
        }

        // Add default menu items at the end
        newMenuItems.push(...defaultMenuItems);

        setMenuItems(newMenuItems);
      } catch (error) {
        console.error("Error fetching packages:", error.message);
        setMenuItems(defaultMenuItems);
      }
    };

    fetchPackagesData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]); // Only depend on 'user', 'defaultMenuItems' doesn't change

  // Extract the base path from the current pathname
  const basePath = location.pathname.split("/")[1];
  let selectedItem = null;

  // Check if there's a matching path in menuItems
  if (basePath) {
    selectedItem = menuItems.find(
      (item) => item.path && item.path.includes(basePath)
    );
  }

  // If no matching path is found, default to the first item in menuItems
  if (!selectedItem) {
    selectedItem = null; // or any specific default item you want to select
  }

  return (
    <div>
      <AppBar position="fixed" open={open}>
        <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            sx={{
              marginRight: 5,
            }}
          >
            <MenuIcon />
          </IconButton>
          <Box>
            <IconButton
              disableRipple
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenu}
              color="inherit"
              sx={{
                justifyContent: "initial",
                alignItems: "initial",
              }}
            >
              <AccountCircle sx={{ mr: 1 }} />
              <Typography sx={{ mr: 1 }}>สินค้าทั้งหมด</Typography>
              <ExpandMoreIcon />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              keepMounted
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              open={Boolean(anchorEl)}
              onClose={handleClose}
              PaperProps={{
                style: {
                  width: "300px",
                },
              }}
            >
              <Grid container alignItems="center" direction="column">
                <Box sx={{ mt: 1 }}>
                  <Avatar className="avatar" alt="User Avatar" src={""} />
                </Box>
                <Box sx={{ my: 2 }}>
                  <Typography style={{ textAlign: "center" }}>
                    {user?.email}
                  </Typography>
                  <Typography
                    style={{ textAlign: "center", color: "lightslategray" }}
                  >
                    {user?.role}
                  </Typography>
                  {/* {console.log("user", user)} */}
                </Box>
                {menuItems.map((item, index) => (
                  <React.Fragment key={index}>
                    {item.isDivider ? <Divider sx={{ width: "100%" }} /> : null}
                    <CustomListItem
                      button
                      selected={selectedItem === item} // Check for direct equality with selectedItem
                      onClick={() => {
                        if (item.onClick) {
                          item.onClick();
                        } else if (item.path) {
                          window.location.href = item.path;
                        }
                        handleClose();
                      }}
                    >
                      <ListItemIcon>{item.icon}</ListItemIcon>
                      <ListItemText primary={item.text} />
                      <ListItemIcon sx={{ justifyContent: "right" }}>
                        {item.NavigateIcon}
                      </ListItemIcon>
                    </CustomListItem>
                  </React.Fragment>
                ))}
              </Grid>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      <ModalSettingCompany
        open={isModalCompanyOpen}
        onClose={handleCompanyClose}
        companyId={user.company_id}
      />

      <ModalBuyPackage
        open={isOpenPackage}
        onClose={handleOpenPackage}
        companyID={user.company_id}
      />

      <ModalChangePassword
        open={isModalChangePassword}
        onClose={handleChangePasswordClose}
      />
    </div>
  );
};

export default Navbar;
